import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock, Star, ArrowLeft, Trophy, Rocket, Target, Shuffle } from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import StarfieldBackground from '../components/effects/StarfieldBackground';
import Button from '../components/common/Button';
import { Card } from '@/components/ui/card';
import type { GameType, Difficulty } from '../types';

const GAME_TYPES: { type: GameType; label: string; symbol: string; description: string; gradient: string; border: string; glow: string; activeGradient: string }[] = [
  {
    type: 'multiplication',
    label: 'Multiply',
    symbol: '×',
    description: 'Times tables',
    gradient: 'from-purple-600/20 to-violet-600/20',
    border: 'border-purple-500/30',
    glow: 'shadow-purple-500/20',
    activeGradient: 'from-purple-600/50 to-violet-600/50',
  },
  {
    type: 'division',
    label: 'Divide',
    symbol: '÷',
    description: 'Share equally',
    gradient: 'from-blue-600/20 to-cyan-600/20',
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20',
    activeGradient: 'from-blue-600/50 to-cyan-600/50',
  },
  {
    type: 'addition',
    label: 'Add',
    symbol: '+',
    description: 'Count together',
    gradient: 'from-emerald-600/20 to-green-600/20',
    border: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/20',
    activeGradient: 'from-emerald-600/50 to-green-600/50',
  },
  {
    type: 'subtraction',
    label: 'Subtract',
    symbol: '−',
    description: 'Take away',
    gradient: 'from-orange-600/20 to-amber-600/20',
    border: 'border-orange-500/30',
    glow: 'shadow-orange-500/20',
    activeGradient: 'from-orange-600/50 to-amber-600/50',
  },
];

const DIFFICULTIES: { level: Difficulty; label: string; emoji: string; hint: string; color: string; activeColor: string }[] = [
  { level: 'easy',   label: 'Easy',   emoji: '🌱', hint: 'Numbers 1–5',  color: 'text-emerald-300', activeColor: 'bg-emerald-500/30 border-emerald-400 text-emerald-200' },
  { level: 'medium', label: 'Medium', emoji: '⚡', hint: 'Numbers 1–10', color: 'text-amber-300',   activeColor: 'bg-amber-500/30 border-amber-400 text-amber-200' },
  { level: 'hard',   label: 'Hard',   emoji: '🔥', hint: 'Numbers 1–12', color: 'text-red-300',     activeColor: 'bg-red-500/30 border-red-400 text-red-200' },
];

export default function LevelSelection() {
  const navigate = useNavigate();
  const {
    currentProfile,
    unlockedLevelsPerGameType,
    levelProgress,
    gameType,
    difficulty,
    setGameType,
    setDifficulty,
  } = useGameStore();

  const levels = Array.from({ length: 10 }, (_, i) => i + 1);
  const unlockedLevels = unlockedLevelsPerGameType[gameType] ?? [1];
  const activeGameType = GAME_TYPES.find(g => g.type === gameType)!;

  const getLevelStars = (level: number) => {
    const progress = levelProgress.get(`${gameType}-${level}`);
    return progress?.stars || 0;
  };

  const getTotalStars = () => {
    let total = 0;
    levelProgress.forEach(progress => { total += progress.stars; });
    return total;
  };

  const getCompletedLevelsForType = () => {
    let count = 0;
    for (let l = 1; l <= 10; l++) {
      if (levelProgress.has(`${gameType}-${l}`)) count++;
    }
    return count;
  };

  const getLevelLabel = (level: number) => {
    switch (gameType) {
      case 'multiplication': return `×${level}`;
      case 'division':       return `÷${level}`;
      case 'addition':       return `+${level}`;
      case 'subtraction':    return `−${level}`;
    }
  };

  return (
    <div className="min-h-screen p-4 relative">
      <StarfieldBackground />
      <div className="max-w-6xl mx-auto relative z-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mr-4">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-4xl font-bold text-white">Select Mission</h1>
          </div>

          {currentProfile && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-full px-4 py-2"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: currentProfile.color }}>
                <img src={currentProfile.avatar} alt={currentProfile.name} className="w-full h-full object-cover" />
              </div>
              <div className="text-right">
                <span className="text-white font-semibold block">{currentProfile.name}</span>
                <span className="text-xs text-purple-300">Space Cadet</span>
              </div>
            </motion.div>
          )}
        </div>

        {/* Game Type Selector */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-5">
          <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest mb-3">Choose Operation</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GAME_TYPES.map((gt, idx) => {
              const isActive = gt.type === gameType;
              return (
                <motion.div
                  key={gt.type}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setGameType(gt.type)}
                >
                  <Card className={`cursor-pointer transition-all duration-300 bg-gradient-to-br ${
                    isActive ? gt.activeGradient : gt.gradient
                  } ${isActive ? gt.border.replace('/30', '') : gt.border} ${
                    isActive ? 'ring-2 ring-white/20 shadow-xl ' + gt.glow : ''
                  } backdrop-blur-md`}>
                    <div className="p-4 flex items-center gap-3">
                      <div className={`text-3xl font-black w-12 h-12 rounded-xl flex items-center justify-center bg-white/10 flex-shrink-0 ${
                        isActive ? 'text-white' : 'text-white/70'
                      }`}>
                        {gt.symbol}
                      </div>
                      <div>
                        <div className={`font-bold text-base ${isActive ? 'text-white' : 'text-white/70'}`}>{gt.label}</div>
                        <div className={`text-xs ${isActive ? 'text-white/70' : 'text-white/40'}`}>{gt.description}</div>
                      </div>
                      {isActive && (
                        <motion.div layoutId="activeGT" className="ml-auto w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Difficulty Selector */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
          <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest mb-3">Difficulty</p>
          <div className="flex gap-3 flex-wrap">
            {DIFFICULTIES.map((d) => {
              const isActive = d.level === difficulty;
              return (
                <motion.button
                  key={d.level}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setDifficulty(d.level)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full border-2 font-semibold transition-all duration-200 text-sm ${
                    isActive
                      ? d.activeColor
                      : 'border-white/20 bg-white/5 text-white/50 hover:border-white/30 hover:text-white/70'
                  }`}
                >
                  <span>{d.emoji}</span>
                  <span>{d.label}</span>
                  <span className={`text-xs font-normal ${isActive ? 'opacity-80' : 'opacity-50'}`}>({d.hint})</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Level Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {levels.map((level, index) => {
            const isUnlocked = unlockedLevels.includes(level);
            const stars = getLevelStars(level);
            const isCompleted = stars > 0;

            return (
              <motion.div
                key={level}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.04 + 0.25 }}
              >
                <motion.div whileHover={isUnlocked ? { scale: 1.05, y: -5 } : {}} whileTap={isUnlocked ? { scale: 0.98 } : {}}>
                  <Card
                    className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${
                      isUnlocked
                        ? isCompleted
                          ? 'bg-gradient-to-br from-emerald-600/20 to-green-600/20 border-emerald-500/50 shadow-lg shadow-emerald-500/20'
                          : `bg-gradient-to-br ${activeGameType.gradient} ${activeGameType.border} hover:border-opacity-60 shadow-lg ${activeGameType.glow}`
                        : 'bg-gray-900/30 cursor-not-allowed border-gray-800/50'
                    } backdrop-blur-md`}
                    onClick={() => isUnlocked && navigate(`/play/${level}`)}
                  >
                    <div className="absolute inset-0 opacity-20">
                      <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full ${
                        isCompleted ? 'bg-emerald-500' : isUnlocked ? 'bg-white' : 'bg-gray-700'
                      } blur-2xl`} />
                    </div>

                    {!isUnlocked && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60 backdrop-blur-sm">
                        <Lock className="w-10 h-10 text-gray-400" />
                      </div>
                    )}

                    <div className="relative p-6">
                      <div className="mb-3 flex justify-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-emerald-500/20' : isUnlocked ? 'bg-white/10' : 'bg-gray-700/20'
                        }`}>
                          {isCompleted ? (
                            <Trophy className="w-6 h-6 text-emerald-400" />
                          ) : isUnlocked ? (
                            <Rocket className="w-6 h-6 text-white/80" />
                          ) : (
                            <Lock className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>

                      <div className="text-center">
                        <div className="text-4xl font-bold text-white mb-2">
                          {getLevelLabel(level)}
                        </div>

                        {isCompleted && (
                          <div className="flex justify-center gap-1 mb-2">
                            {[1, 2, 3].map((star) => (
                              <motion.div
                                key={star}
                                initial={{ rotate: 0 }}
                                animate={{ rotate: star <= stars ? 360 : 0 }}
                                transition={{ duration: 0.5, delay: star * 0.1 }}
                              >
                                <Star className={`w-5 h-5 ${star <= stars ? 'text-yellow-400 fill-yellow-400 drop-shadow-lg' : 'text-gray-600'}`} />
                              </motion.div>
                            ))}
                          </div>
                        )}

                        {!isCompleted && isUnlocked && (
                          <motion.div
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="text-sm font-semibold text-white/70"
                          >
                            START
                          </motion.div>
                        )}

                        {isCompleted && (
                          <div className="text-xs text-emerald-400 font-semibold">COMPLETE</div>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Random Mix */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="mb-8">
          <motion.div whileHover={{ scale: 1.02, y: -3 }} whileTap={{ scale: 0.98 }}>
            <Card
              className={`relative overflow-hidden cursor-pointer bg-gradient-to-r ${activeGameType.activeGradient} via-pink-600/20 ${activeGameType.border.replace('/30', '/40')} hover:border-opacity-60 shadow-lg ${activeGameType.glow} backdrop-blur-md transition-all duration-300`}
              onClick={() => navigate('/play/random')}
            >
              <div className="absolute inset-0 opacity-20">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/30 rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-pink-500 rounded-full blur-3xl" />
              </div>
              <div className="relative p-6 flex items-center gap-6">
                <div className={`w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/20`}>
                  <Shuffle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-1">Random Mix — {activeGameType.label}</h3>
                  <p className="text-white/60 text-sm">10 random questions · {DIFFICULTIES.find(d => d.level === difficulty)?.label} difficulty</p>
                </div>
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-sm font-bold text-white bg-white/10 rounded-full px-4 py-2 border border-white/20 flex-shrink-0"
                >
                  START
                </motion.div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
          <Card className="relative overflow-hidden p-8 bg-gradient-to-r from-purple-900/40 to-pink-900/40 backdrop-blur-md border-purple-500/30">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute -top-20 -left-20 w-40 h-40 bg-purple-500 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-pink-500 rounded-full blur-3xl" />
            </div>

            <div className="relative flex justify-around text-center">
              <motion.div whileHover={{ scale: 1.05 }}>
                <div className="bg-yellow-500/10 rounded-2xl p-4 backdrop-blur-sm border border-yellow-500/20">
                  <Star className="w-8 h-8 text-yellow-400 fill-yellow-400 mx-auto mb-2" />
                  <span className="text-4xl font-bold text-white block">{getTotalStars()}</span>
                  <p className="text-sm text-yellow-300 mt-1">Total Stars</p>
                </div>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }}>
                <div className="bg-purple-500/10 rounded-2xl p-4 backdrop-blur-sm border border-purple-500/20">
                  <Trophy className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <span className="text-4xl font-bold text-white block">{getCompletedLevelsForType()}/10</span>
                  <p className="text-sm text-purple-300 mt-1">{activeGameType.label} Missions</p>
                </div>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }}>
                <div className="bg-emerald-500/10 rounded-2xl p-4 backdrop-blur-sm border border-emerald-500/20">
                  <Target className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <div className="text-4xl font-bold text-white">
                    {Math.round((getCompletedLevelsForType() / 10) * 100)}%
                  </div>
                  <p className="text-sm text-emerald-300 mt-1">Progress</p>
                </div>
              </motion.div>
            </div>
          </Card>
        </motion.div>

      </div>
    </div>
  );
}